import { resolvePadSizeId } from "./annotationRenderer.js";
import { mskTodayStr } from "./mskDate.js";
import { reportProducts, reportProductsTotal } from "./reportProducts.js";

// Crop a page image to a region annotation (natural image pixels)
export async function cropFootImage(base64, region) {
  if (!base64 || !region) return null;
  try {
    const x = Math.round(Math.min(region.x1, region.x2));
    const y = Math.round(Math.min(region.y1, region.y2));
    const w = Math.round(Math.abs(region.x2 - region.x1));
    const h = Math.round(Math.abs(region.y2 - region.y1));
    if (w < 2 || h < 2) return null;

    const src = base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`;
    // fetch → blob → createImageBitmap crops in one step, avoids canvas-taint issues
    const resp = await fetch(src);
    const blob = await resp.blob();
    const bitmap = await createImageBitmap(blob, x, y, w, h);

    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    bitmap.close();

    return canvas.toDataURL("image/jpeg", 0.88);
  } catch (e) {
    console.error("cropFootImage failed:", e);
    return null;
  }
}

const MEAS_LABELS = {
  size_line: "Длина стопы",
  width_line: "Ширина",
  heel_line: "Ширина пяточной области",
};

function mmToCm(mm) {
  return mm ? (Number(mm) / 10).toFixed(1) + " см" : "—";
}

function escapeHtml(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

function productRow(product) {
  const money = value => value === null ? "Цена не указана" : `${Number(value).toLocaleString("ru-RU")} ₽`;
  return `<div class="product-row" style="align-items:flex-start">
    <div style="flex:1;min-width:0">
      <div>${escapeHtml(product.name)}${product.article ? ` <span class="product-article">арт. ${escapeHtml(product.article)}</span>` : ""}</div>
      <div style="font-size:10px;color:#607d8b;margin-top:4px">${product.quantity} ${product.category === "insole" ? "пар." : "шт."} × ${money(product.price)}</div>
    </div>
    <span class="product-price" style="white-space:nowrap">${money(product.total)}</span>
  </div>`;
}


function footBlock(side, originalSrc, heatSrc, params, annotations, padSizes, activePadSizeId) {
  const label = side === "left" ? "ЛЕВАЯ СТОПА" : "ПРАВАЯ СТОПА";
  const accentCls = side === "left" ? "left-accent" : "right-accent";
  const data = params[side] || {};
  // жалобы redesign: new-format scans (complaintsFormat 2) carry symptoms/footTypes/cornZones;
  // pre-redesign scans carry diseases/complaints/complaintsText/otherText instead. footBlock only
  // ever sees this per-foot object (not the document row), so the shape itself is the signal.
  const isNewFormat = "symptoms" in data || "footTypes" in data || "cornZones" in data;
  const diseases = data.diseases || [];
  const complaints = data.complaints || [];
  const complaintsText = data.complaintsText || "";
  const otherText = data.otherText || "";

  const measurements = (annotations || [])
    .filter(a => {
      const forSide = side === "left" ? (a.foot === "left" || !a.foot) : a.foot === "right";
      return forSide && MEAS_LABELS[a.type];
    });

  const scanPair = `
    <div class="scan-pair">
      <div class="scan-box">
        <div class="scan-label">Обычный скан</div>
        ${originalSrc
          ? `<img src="${originalSrc}" class="scan-img" alt="скан">`
          : `<div class="scan-empty">Область не указана</div>`}
      </div>
      <div class="scan-box">
        <div class="scan-label">Тепловая карта</div>
        ${heatSrc
          ? `<img src="${heatSrc}" class="scan-img" alt="тепловая карта">`
          : `<div class="scan-empty">Нет данных</div>`}
      </div>
    </div>`;

  const measBlock = measurements.length > 0 ? `
    <div class="section-block">
      <div class="block-header">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2.5"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
        Измерения
      </div>
      <table class="meas-table">
        ${measurements.map(a => `
          <tr>
            <td class="meas-name">${MEAS_LABELS[a.type]}</td>
            <td class="meas-val">${mmToCm(a.distanceMm)}</td>
          </tr>`).join("")}
      </table>
    </div>` : "";

  const padBlock = padSizes.length > 0 ? `
    <div class="pad-row">
      <div class="pad-label">Тип метатарзального валика</div>
      <div class="pad-pills">
        ${padSizes.map(ps => {
          const isActive = activePadSizeId ? ps.id === activePadSizeId : false;
          return `<span class="pad-pill${isActive ? " active" : ""}">${escapeHtml(ps.name)}${ps.length_mm && ps.width_mm ? ` — ${ps.length_mm}×${ps.width_mm} мм` : ""}</span>`;
        }).join("")}
      </div>
    </div>` : "";

  const complaintIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
  const namedListBlock = (title, items) => `
    <div class="section-block">
      <div class="block-header">${complaintIcon}${title}</div>
      ${items.length > 0
        ? `<div class="complaints-text">${escapeHtml(items.join(", "))}.</div>`
        : `<div class="no-dev">Не указаны</div>`}
    </div>`;

  const complaintsBlock = isNewFormat
    ? namedListBlock("Симптомы", data.symptoms || []) +
      namedListBlock("Тип стопы", data.footTypes || []) +
      namedListBlock("Натоптыши по зонам", data.cornZones || [])
    : namedListBlock("Жалобы пациента", [...diseases, otherText, ...complaints, complaintsText].filter(Boolean));

  return `
    <div class="foot-section ${accentCls}">
      <div class="foot-title">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M13 22h-2a9 9 0 0 1-9-9V7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v2a9 9 0 0 1-9 9z" opacity=".3"/><circle cx="9" cy="7" r="1.5"/><circle cx="13" cy="5" r="1.5"/><circle cx="17" cy="7" r="1.5"/></svg>
        ${label}
      </div>
      ${scanPair}
      ${measBlock}
      ${padBlock}
      ${complaintsBlock}
    </div>`;
}

export function generateReportHtml({
  // New-format fields
  leftOriginal = null, leftHeat = null,
  rightOriginal = null, rightHeat = null,
  params = {},
  padSizes = [],
  padSizesPerFoot = { left: null, right: null },
  products = [],
  productDetails = {},
  loadTypes = [],
  exercises = [],
  // Shared fields
  patient = null,
  annotations = [],
  dateScanned = null,
  // Legacy fields (doSave still passes these)
  imageUrl = null,
  footData = null,
  generalSymptoms: legacyGeneralSymptoms = null,
  noteComment: legacyNoteComment = null,
  noteRecommendations: legacyNoteRecommendations = null,
} = {}) {
  // Merge legacy fields into params if params is empty (doSave compatibility)
  const resolvedParams = {
    left:  (params.left  || footData?.left  || {}),
    right: (params.right || footData?.right || {}),
    shoeTypes:          params.shoeTypes          || [],
    generalSymptoms:    params.generalSymptoms     || legacyGeneralSymptoms || [],
    noteComment:        params.noteComment         || legacyNoteComment || "",
    noteRecommendations:params.noteRecommendations || legacyNoteRecommendations || "",
    ...params,
  };

  const name = patient?.alias?.trim() || patient?.name || "—";
  const birthDate = patient?.birthDate
    ? new Date(patient.birthDate + "T00:00:00").toLocaleDateString("ru-RU")
    : "—";
  const scanDate = dateScanned
    ? new Date(dateScanned).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })
    : new Date().toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });

  const shoeTypes = resolvedParams.shoeTypes || [];
  const noteComment = resolvedParams.noteComment || "";
  const noteRecommendations = resolvedParams.noteRecommendations || "";

  const allProducts = reportProducts(products || [], productDetails);
  const productsTotal = reportProductsTotal(allProducts);
  const insoleProducts = allProducts.filter(p => p.category === "insole");
  const accessoryProducts = allProducts.filter(p => p.category !== "insole");
  const selectedLoadType = loadTypes.find(lt => lt.id === resolvedParams.loadTypeId) || null;
  const reportId = `PS-${mskTodayStr().replace(/-/g, "")}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

  // For legacy save-mode: use imageUrl in place of foot images
  const effectiveLeftOrig  = leftOriginal  || imageUrl || null;
  const effectiveRightOrig = rightOriginal || null;

  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<title>Отчет по диагностике стоп — ${escapeHtml(name)}</title>
<style>
* { box-sizing: border-box; margin: 0; padding: 0; }
body {
  font-family: -apple-system, "Helvetica Neue", Arial, sans-serif;
  font-size: 11px;
  color: #1a1a2e;
  background: #f0f2f8;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.page { max-width: 960px; margin: 0 auto; background: white; padding: 24px 28px; }

/* ── Header ── */
.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 2px solid #e8eaf6;
  margin-bottom: 14px;
}
.logo-mark { display: flex; align-items: center; gap: 10px; }
.logo-svg { width: 130px; height: auto; flex-shrink: 0; }
.logo-text-name { font-size: 21px; font-weight: 900; color: #1a237e; letter-spacing: -0.5px; line-height: 1; }
.logo-text-sub { font-size: 9.5px; color: #7986cb; margin-top: 2px; }
.title-right { text-align: right; }
.title-right h1 { font-size: 17px; font-weight: 700; color: #1a237e; }
.title-right p { font-size: 10px; color: #9fa8da; margin-top: 2px; }

/* ── Patient bar ── */
.patient-bar {
  display: flex;
  align-items: flex-start;
  gap: 0;
  background: #f5f5ff;
  border: 1px solid #e0e4f8;
  border-radius: 10px;
  padding: 12px 20px;
  margin-bottom: 18px;
  gap: 24px;
  flex-wrap: wrap;
}
.pf { display: flex; flex-direction: column; gap: 3px; }
.pf label { font-size: 8.5px; color: #9fa8da; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 700; }
.pf .val { font-size: 13px; font-weight: 700; color: #1a237e; }

/* ── Feet row ── */
.feet-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 18px; }
.foot-section { border: 1px solid #e0e4f8; border-radius: 12px; overflow: hidden; }
.foot-section.left-accent  { border-top: 3px solid #3B82F6; }
.foot-section.right-accent { border-top: 3px solid #22C55E; }

.foot-title {
  display: flex; align-items: center; gap: 7px;
  font-size: 12px; font-weight: 800; letter-spacing: 0.5px;
  padding: 9px 14px;
  background: #f8f9ff;
  border-bottom: 1px solid #e8eaf6;
}
.left-accent  .foot-title { color: #1d4ed8; }
.right-accent .foot-title { color: #15803d; }

.scan-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding: 10px; }
.scan-box { display: flex; flex-direction: column; align-items: center; }
.scan-label {
  font-size: 9px; font-weight: 600; color: #7c8db0;
  background: #eef2ff; border-radius: 10px;
  padding: 2px 8px; margin-bottom: 5px;
}
.scan-img { width: 100%; max-height: 210px; object-fit: contain; border-radius: 8px; border: 1px solid #e8eaf6; background: #ffffff; }
.scan-empty {
  width: 100%; height: 170px;
  display: flex; align-items: center; justify-content: center;
  color: #c5cae9; font-size: 10px;
  background: #f8fafb; border: 1.5px dashed #dde1f0; border-radius: 8px;
}

.section-block { padding: 8px 14px; border-top: 1px solid #f0f2ff; }
.block-header {
  font-size: 10px; font-weight: 700; color: #5c6bc0;
  display: flex; align-items: center; gap: 5px;
  margin-bottom: 6px;
}

.meas-table { width: 100%; border-collapse: collapse; }
.meas-table tr { border-bottom: 1px solid #f4f5ff; }
.meas-table tr:last-child { border-bottom: none; }
.meas-name { color: #546e7a; font-size: 10.5px; padding: 3px 0; }
.meas-val  { text-align: right; font-size: 11px; font-weight: 700; color: #1a237e; }

.pad-row { padding: 8px 14px; border-top: 1px solid #f0f2ff; }
.pad-label { font-size: 9px; color: #9fa8da; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 700; margin-bottom: 5px; }
.pad-pills { display: flex; gap: 4px; flex-wrap: wrap; }
.pad-pill {
  padding: 3px 10px; border-radius: 20px;
  font-size: 9.5px; font-weight: 600;
  border: 1.5px solid #c5cae9; color: #7986cb; background: white;
}
.pad-pill.active { background: #3B82F6; color: white; border-color: #3B82F6; }

.no-dev { font-size: 9.5px; color: #b0bec5; margin-top: 4px; font-style: italic; }
.complaints-text { font-size: 10.5px; color: #37474f; line-height: 1.5; }

/* ── Bottom grid ── */
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 12px;
}
.bottom-card { border: 1px solid #e0e4f8; border-radius: 10px; padding: 12px 14px; }
.bottom-card h3 {
  font-size: 9.5px; font-weight: 700; color: #5c6bc0;
  text-transform: uppercase; letter-spacing: 0.4px;
  margin-bottom: 8px;
  display: flex; align-items: center; gap: 5px;
  border-bottom: 1px solid #f0f2ff; padding-bottom: 6px;
}

.insole-name { font-size: 12px; font-weight: 700; color: #1a237e; margin-bottom: 3px; line-height: 1.3; }
.insole-price { font-size: 10px; color: #43a047; font-weight: 600; margin-bottom: 6px; }
.support-badge {
  display: inline-block; margin-top: 4px;
  padding: 3px 10px; border-radius: 20px;
  background: #3B82F6; color: white;
  font-size: 9px; font-weight: 700;
}

.product-row {
  display: flex; align-items: center; gap: 6px;
  padding: 3px 0; border-bottom: 1px solid #f4f5ff;
  font-size: 10.5px; color: #37474f;
}
.product-row:last-child { border-bottom: none; }
.product-price { color: #43a047; font-weight: 700; font-size: 10px; white-space: nowrap; }
.product-photo { width: 22px; height: 22px; border-radius: 5px; object-fit: cover; flex-shrink: 0; }
.product-article { color: #9fa8da; font-size: 9px; font-weight: 500; }


.conclusion-text { font-size: 10.5px; color: #37474f; line-height: 1.55; }
.comments-text   { font-size: 10.5px; color: #37474f; line-height: 1.55; }
.empty-note { font-size: 10px; color: #c5cae9; font-style: italic; }

/* ── Exercises page ── */
.exercises-page { margin-top: 18px; padding-top: 4px; }
.exercises-title {
  display: flex; align-items: center; gap: 7px;
  font-size: 13px; font-weight: 800; color: #1a237e;
  padding-bottom: 8px; margin-bottom: 12px;
  border-bottom: 2px solid #e8eaf6;
}
.exercises-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.exercise-card { border: 1px solid #e0e4f8; border-radius: 12px; padding: 12px; }
.exercise-photo { width: 100%; height: 140px; object-fit: cover; border-radius: 8px; margin-bottom: 8px; border: 1px solid #e8eaf6; }
.exercise-photo-empty {
  width: 100%; height: 90px; margin-bottom: 8px;
  display: flex; align-items: center; justify-content: center;
  color: #c5cae9; font-size: 10px;
  background: #f8fafb; border: 1.5px dashed #dde1f0; border-radius: 8px;
}
.exercise-name { font-size: 12px; font-weight: 700; color: #1a237e; margin-bottom: 4px; }
.exercise-desc { font-size: 10.5px; color: #546e7a; line-height: 1.5; margin-bottom: 6px; white-space: pre-wrap; }
.exercise-link { font-size: 10.5px; color: #3B82F6; text-decoration: underline; }

@media print {
  .exercises-page { page-break-before: always; }
}

/* ── Footer ── */
.report-footer {
  margin-top: 18px; padding-top: 10px;
  border-top: 1px solid #e8eaf6;
  display: flex; justify-content: space-between; align-items: flex-end;
}
.footer-note { font-size: 8.5px; color: #b0bec5; line-height: 1.5; max-width: 500px; }
.footer-id { font-size: 9px; color: #9fa8da; font-weight: 700; white-space: nowrap; }

@media print {
  body { background: white; }
  .page { max-width: none; padding: 10mm 12mm; }
}
</style>
</head>
<body>
<div class="page">

  <!-- ШАПКА -->
  <header class="report-header">
        <div class="logo-mark">
      <svg class="logo-svg" viewBox="0 0 267 89" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_2886_322)">
          <path d="M136.705 28.48C141.177 25.8323 144.181 20.915 144.181 15.2635C144.181 9.612 138.841 1.44625 131.788 0.2225C130.964 0.06675 130.097 0 129.229 0H105.822C105.243 0 104.776 0.4895 104.776 1.068V59.0738C104.776 59.6745 105.243 60.1418 105.822 60.1418H131.498C140.309 60.1418 147.741 52.7993 147.674 43.8325C147.63 36.668 143.046 30.5938 136.683 28.48H136.705ZM128.472 6.60825C129.674 6.8085 130.764 7.23125 131.788 7.832C134.658 9.523 136.594 12.6825 136.594 16.3093C136.594 19.936 134.658 23.0955 131.788 24.7865C130.764 25.3873 129.629 25.81 128.428 25.988H111.74C111.162 25.988 110.695 25.4985 110.695 24.92V7.6985C110.695 7.09775 111.162 6.6305 111.74 6.6305H128.472V6.60825ZM128.317 32.396H129.251C130.141 32.396 130.987 32.5073 131.81 32.7075C136.438 33.8645 140.51 38.1365 140.51 43.2095C140.51 48.2825 136.416 52.5322 131.81 53.7115C130.987 53.9118 130.141 54.023 129.251 54.023H111.763C111.184 54.023 110.717 53.5335 110.717 52.955V33.4863C110.717 32.8855 111.184 32.4183 111.763 32.4183H128.339L128.317 32.396Z" fill="#0D0C34"/>
          <path d="M202.03 49.2838L204.878 52.1318C205.457 52.7103 205.412 53.667 204.767 54.201C199.538 58.562 192.863 61.2098 185.565 61.321C168.722 61.5435 154.727 47.971 154.482 31.1278C154.215 13.973 168.032 0 185.12 0C202.208 0 199.56 2.7145 204.9 7.23125C205.523 7.76525 205.546 8.722 204.967 9.27825L202.141 12.104C201.63 12.6158 200.806 12.638 200.25 12.193C196 8.69975 190.527 6.65275 184.564 6.8085C171.904 7.09775 161.58 17.4217 161.268 30.082C160.934 43.521 171.748 54.5348 185.12 54.5348C198.492 54.5348 196.045 52.5323 200.139 49.217C200.695 48.7498 201.518 48.7943 202.03 49.306V49.2838Z" fill="#0D0C34"/>
          <path d="M256.187 6.60825V0H214.09V60.1418H256.187V53.9785H221.276V32.3515H249.156V25.9435H221.276V6.43025L256.187 6.60825Z" fill="#0D0C34"/>
          <path d="M118.192 85.5965L118.993 86.3975C119.149 86.5532 119.149 86.8425 118.971 86.976C117.502 88.1997 115.611 88.9562 113.542 88.9785C108.78 89.0452 104.842 85.2182 104.775 80.4567C104.709 75.6285 108.602 71.668 113.431 71.668C118.259 71.668 117.502 72.4467 119.015 73.715C119.193 73.8707 119.193 74.1377 119.015 74.2935L118.214 75.0945C118.059 75.228 117.836 75.2502 117.68 75.1167C116.479 74.1377 114.944 73.5592 113.253 73.6037C109.693 73.6927 106.778 76.6075 106.689 80.1675C106.6 83.95 109.648 87.065 113.408 87.065C117.169 87.065 116.501 86.5087 117.658 85.552C117.814 85.4185 118.036 85.4407 118.192 85.5742V85.5965Z" fill="#0D0C34"/>
          <path d="M149.209 73.3352V71.9779C149.209 71.8222 149.097 71.7109 148.942 71.7109H137.127C136.971 71.7109 136.86 71.8222 136.86 71.9779V88.7544C136.86 88.9102 136.971 89.0214 137.127 89.0214H148.942C149.097 89.0214 149.209 88.9102 149.209 88.7544V87.5084C149.209 87.3527 149.097 87.2414 148.942 87.2414H139.241C139.085 87.2414 138.974 87.1302 138.974 86.9744V81.2784C138.974 81.1227 139.085 81.0114 139.241 81.0114H146.872C147.028 81.0114 147.139 80.9002 147.139 80.7444V79.4317C147.139 79.2759 147.028 79.1647 146.872 79.1647H139.241C139.085 79.1647 138.974 79.0534 138.974 78.8977V73.8247C138.974 73.6689 139.085 73.5577 139.241 73.5577L148.942 73.6022C149.097 73.6022 149.209 73.4909 149.209 73.3352Z" fill="#0D0C34"/>
          <path d="M126.558 88.6002V73.9375C126.558 73.715 126.38 73.537 126.158 73.537H121.819C121.596 73.537 121.418 73.359 121.418 73.1365V72.0685C121.418 71.846 121.596 71.668 121.819 71.668H133.389C133.611 71.668 133.789 71.846 133.789 72.0685V73.181C133.789 73.4035 133.611 73.5815 133.389 73.5815L129.05 73.537C128.828 73.537 128.65 73.715 128.65 73.9375V88.6002C128.65 88.8227 128.472 89.0007 128.249 89.0007H126.914C126.692 89.0007 126.514 88.8227 126.514 88.6002H126.558Z" fill="#0D0C34"/>
          <path d="M219.652 86.5977H219.074C218.607 86.5977 218.228 86.9762 218.228 87.4432V88.1552C218.228 88.6221 218.607 89.0007 219.074 89.0007H219.652C220.119 89.0007 220.498 88.6221 220.498 88.1552V87.4432C220.498 86.9762 220.119 86.5977 219.652 86.5977Z" fill="#0D0C34"/>
          <path d="M165.562 88.7344H163.916C163.76 88.7344 163.627 88.6009 163.627 88.4451V74.2941C163.627 74.1384 163.493 74.0049 163.337 74.0049H156.551C156.395 74.0049 156.262 74.1384 156.262 74.2941V84.4846C156.262 87.4884 155.172 89.0014 152.991 89.0014C150.811 89.0014 152.101 88.9569 151.612 88.8679C151.478 88.8456 151.367 88.7344 151.367 88.5786V87.2214C151.367 87.0434 151.523 86.9099 151.701 86.9544C151.945 86.9989 152.235 87.0211 152.546 87.0211C153.102 87.0211 153.481 86.8209 153.725 86.4426C153.97 86.0644 154.059 85.4191 154.059 84.5514V72.3361C154.059 72.1804 154.193 72.0469 154.348 72.0469H165.54C165.696 72.0469 165.829 72.1804 165.829 72.3361V88.4674C165.829 88.6231 165.696 88.7566 165.54 88.7566L165.562 88.7344Z" fill="#0D0C34"/>
          <path d="M171.815 78.5862C171.815 78.7864 171.97 78.9422 172.171 78.9422H175.664C180.181 78.9422 182.45 80.6109 182.45 83.9707C182.45 87.3304 181.894 86.8409 180.804 87.7087C179.713 88.5764 178.111 88.9992 175.998 88.9992H169.857C169.679 88.9992 169.523 88.8434 169.523 88.6654V72.0447C169.523 71.8667 169.679 71.7109 169.857 71.7109H171.481C171.659 71.7109 171.815 71.8667 171.815 72.0447V78.6084V78.5862ZM171.815 86.5962C171.815 86.7742 171.97 86.9299 172.148 86.9299H175.975C178.712 86.9299 180.069 85.9287 180.069 83.9484C180.069 81.9682 179.758 82.1684 179.113 81.7012C178.49 81.2339 177.333 80.9892 175.664 80.9892H172.126C171.948 80.9892 171.792 81.1449 171.792 81.3229V86.6184L171.815 86.5962Z" fill="#0D0C34"/>
          <path d="M224.658 88.9785H223.056C222.856 88.9785 222.7 88.8227 222.7 88.6225V72.024C222.7 71.8237 222.856 71.668 223.056 71.668H229.175C231.267 71.668 232.869 72.0907 233.981 72.9585C235.071 73.8262 235.628 75.0722 235.628 76.6965C235.628 80.034 233.358 81.725 228.841 81.725H225.348C225.148 81.725 224.992 81.8807 224.992 82.081V88.6225C224.992 88.8227 224.836 88.9785 224.636 88.9785H224.658ZM225.014 79.3442C225.014 79.5445 225.17 79.7002 225.37 79.7002H228.886C230.555 79.7002 231.712 79.4555 232.335 78.9882C232.958 78.521 233.291 77.7645 233.291 76.741C233.291 74.7607 231.912 73.7595 229.197 73.7595H225.393C225.192 73.7595 225.037 73.9152 225.037 74.1155V79.3665L225.014 79.3442Z" fill="#0D0C34"/>
          <path d="M199.004 89.0004H196.757C196.668 89.0004 196.579 88.9559 196.512 88.8669L190.683 80.6789C190.571 80.5231 190.349 80.5009 190.215 80.6344L187.879 82.9261C187.812 82.9929 187.79 83.0596 187.79 83.1486V88.6889C187.79 88.8669 187.657 89.0004 187.479 89.0004H185.81C185.632 89.0004 185.498 88.8669 185.498 88.6889V72.0459C185.498 71.8679 185.632 71.7344 185.81 71.7344H187.479C187.657 71.7344 187.79 71.8679 187.79 72.0459V79.5664C187.79 79.8334 188.124 79.9669 188.302 79.7889L196.112 71.8456C196.178 71.7789 196.245 71.7566 196.334 71.7566H198.559C198.826 71.7566 198.96 72.0904 198.782 72.2684L192.285 78.6541C192.173 78.7654 192.173 78.9211 192.262 79.0546L199.293 88.5331C199.449 88.7334 199.293 89.0226 199.049 89.0226L199.004 89.0004Z" fill="#0D0C34"/>
          <path d="M215.78 88.9778H214.112C213.978 88.9778 213.867 88.8665 213.867 88.733V76.006C213.867 75.7613 213.533 75.6723 213.4 75.8725L204.923 88.8888C204.878 88.9555 204.789 89 204.722 89H202.787C202.653 89 202.542 88.8888 202.542 88.7553V72.0455C202.542 71.912 202.653 71.8008 202.787 71.8008H204.455C204.589 71.8008 204.7 71.912 204.7 72.0455V84.7725C204.7 85.0173 205.034 85.1063 205.167 84.906L213.645 71.912C213.689 71.8453 213.778 71.8008 213.845 71.8008H215.803C215.936 71.8008 216.047 71.912 216.047 72.0455V88.7553C216.047 88.8888 215.936 89 215.803 89L215.78 88.9778Z" fill="#0D0C34"/>
          <path d="M248.31 73.2489C248.31 73.4269 248.444 73.5382 248.599 73.5604C250.98 73.6049 252.805 74.2057 254.117 75.3404C255.475 76.5419 256.164 78.1439 256.164 80.1909C256.164 82.2379 255.475 83.7954 254.14 84.9524C252.849 86.0649 251.002 86.6434 248.599 86.6879C248.421 86.6879 248.31 86.8214 248.31 86.9994V88.6682C248.31 88.8462 248.177 88.9797 247.999 88.9797H246.33C246.152 88.9797 246.018 88.8462 246.018 88.6682V86.9994C246.018 86.8214 245.885 86.6879 245.729 86.6879C243.348 86.6434 241.502 86.0649 240.189 84.9524C238.832 83.7954 238.142 82.2157 238.142 80.1909C238.142 78.1662 238.832 76.5197 240.189 75.3404C241.502 74.2057 243.348 73.6049 245.707 73.5604C245.885 73.5604 245.996 73.4269 245.996 73.2489V72.0029C245.996 71.8249 246.13 71.6914 246.308 71.6914H247.976C248.154 71.6914 248.288 71.8249 248.288 72.0029V73.2489H248.31ZM246.041 84.4629V75.7632C246.041 75.5852 245.907 75.4517 245.729 75.4517C244.038 75.4962 242.748 75.8522 241.88 76.5642C240.968 77.2984 240.5 78.4777 240.5 80.1242C240.5 81.7707 240.968 82.9944 241.88 83.7064C242.748 84.3739 244.038 84.7299 245.729 84.7744C245.907 84.7744 246.041 84.6409 246.041 84.4629ZM248.31 84.4629C248.31 84.6409 248.444 84.7744 248.622 84.7744C250.335 84.7299 251.603 84.3739 252.471 83.7064C253.383 82.9944 253.85 81.7929 253.85 80.1242C253.85 78.4554 253.383 77.2984 252.471 76.5642C251.603 75.8744 250.313 75.4962 248.622 75.4517C248.444 75.4517 248.31 75.5852 248.31 75.7632V84.4629Z" fill="#0D0C34"/>
          <path d="M75.65 76.7845C75.4942 77.0292 75.294 77.2295 75.0715 77.3852C69.4422 81.3012 63.19 74.871 61.321 72.9575C59.452 71.044 49.395 62.6558 46.28 60.453C43.1872 58.2725 35.2885 52.621 33.6198 50.9077C31.951 49.1945 25.4985 40.6282 22.161 35.2215C19.847 31.5057 21.4267 27.523 22.606 25.4537C23.1845 24.4302 24.03 23.5625 25.0312 22.9617C27.0782 21.738 30.9275 20.1137 34.5542 22.4945C39.8053 25.921 48.149 32.5515 49.84 34.2425C51.531 35.9335 57.0045 44.0547 59.1405 47.2365C61.2765 50.4182 69.3977 60.72 71.3112 62.678C73.2247 64.636 79.4548 71.0217 75.65 76.7845Z" fill="#0D0C34"/>
          <path d="M81.9023 6.83134C80.9678 2.51484 77.1185 0.846094 74.8268 0.245344C73.7143 -0.0661563 72.535 -0.0661563 71.4003 0.245344C69.1308 0.846094 65.2815 2.51484 64.3248 6.83134C62.9675 13.0613 61.6325 23.8081 61.6325 26.2333C61.6325 28.6586 63.3235 38.3596 63.991 42.1643C64.0578 42.5426 64.1245 43.0098 64.2135 43.5661C64.5473 45.8578 65.4595 48.0383 66.8835 49.8628C69.6648 53.4228 72.624 57.0496 73.692 58.1398C74.76 59.2301 77.3188 61.8556 78.5203 64.9706C78.587 65.1486 78.7205 65.3044 78.8763 65.3933C79.032 65.4823 79.2323 65.5268 79.4103 65.5046C79.5883 65.4823 79.7663 65.3933 79.8998 65.2376C80.0333 65.1041 80.1 64.9038 80.1223 64.7258C80.2113 63.5688 80.2113 62.5676 80.2113 61.9446C80.2113 59.1856 81.5685 46.0136 82.236 42.1866C82.9035 38.3818 84.5945 28.7031 84.5945 26.2556C84.5945 23.8081 83.2373 13.0613 81.9023 6.83134Z" fill="#0D0C34"/>
          <path d="M57.7831 76.607C56.9376 75.7392 54.5123 73.6255 51.7978 71.356C49.9288 69.7985 47.6593 68.7972 45.2786 68.4857C43.4986 68.2632 42.0078 68.0407 41.0733 67.8627C37.3576 67.173 27.9236 65.4375 25.5428 65.4375C23.1621 65.4375 12.7268 66.8393 6.63031 68.2188C2.42506 69.131 0.800811 73.0915 0.200061 75.4277C-0.0891887 76.5847 -0.0891887 77.7862 0.200061 78.9432C0.800811 81.2795 2.40281 85.24 6.63031 86.1967C12.7046 87.5985 23.1621 88.978 25.5428 88.978C27.9236 88.978 37.3576 87.2425 41.0733 86.5527C44.7891 85.863 57.6051 84.4613 60.3196 84.4613C62.4333 84.5058 64.5471 84.2832 66.5941 83.7715C66.7721 83.727 66.9501 83.6157 67.0613 83.4377C67.1726 83.282 67.2393 83.0817 67.2171 82.8815C67.2171 82.6812 67.1281 82.5033 66.9946 82.3475C66.8611 82.214 66.6831 82.125 66.4828 82.1027C62.5891 81.4352 59.1181 77.8752 57.8053 76.5402L57.7831 76.607Z" fill="#0D0C34"/>
        </g>
        <defs>
          <clipPath id="clip0_2886_322">
            <rect width="267" height="89" fill="white"/>
          </clipPath>
        </defs>
      </svg>
      <div>
        <div class="logo-text-sub">Технологии точной диагностики стоп</div>
      </div>
    </div>
    <div class="title-right">
      <h1>Отчет по диагностике стоп</h1>
      <p>Персональный отчет пациента</p>
    </div>
  </header>

  <!-- ДАННЫЕ ПАЦИЕНТА -->
  <div class="patient-bar">
    <div class="pf" style="flex:2;min-width:150px">
      <label>ФИО пациента</label>
      <span class="val" style="font-size:14px">${escapeHtml(name)}</span>
    </div>
    <div class="pf">
      <label>Дата рождения</label>
      <span class="val">${birthDate}</span>
    </div>
    <div class="pf">
      <label>Дата сканирования</label>
      <span class="val">${scanDate}</span>
    </div>
    <div class="pf">
      <label>Вес</label>
      <span class="val">${resolvedParams.weight ? resolvedParams.weight + " кг" : "—"}</span>
    </div>
    <div class="pf" style="flex:1.5;min-width:120px">
      <label>Типы обуви</label>
      <span class="val" style="font-size:12px">${shoeTypes.length ? shoeTypes.map(escapeHtml).join(", ") : "—"}</span>
    </div>
  </div>

  <!-- СКАНЫ СТОП -->
  <div class="feet-row">
    ${footBlock("left",  effectiveLeftOrig,  leftHeat,  resolvedParams, annotations, padSizes, resolvePadSizeId(padSizes, padSizesPerFoot, "left"))}
    ${footBlock("right", effectiveRightOrig, rightHeat, resolvedParams, annotations, padSizes, resolvePadSizeId(padSizes, padSizesPerFoot, "right"))}
  </div>

  ${(resolvedParams.generalSymptoms || []).length > 0 ? `
  <!-- ОБЩИЕ СИМПТОМЫ (не привязаны к конкретной стопе) -->
  <div class="section-block" style="margin-bottom: 18px;">
    <div class="block-header">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      Общие симптомы
    </div>
    <div class="complaints-text">${escapeHtml(resolvedParams.generalSymptoms.join(", "))}.</div>
  </div>` : ""}

  <!-- НИЖНЯЯ СЕКЦИЯ -->
  <div class="bottom-grid">

    <!-- Рекомендуемые стельки -->
    <div class="bottom-card">
      <h3>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2"><path d="M3 7l9-4 9 4v13l-9 4-9-4z"/></svg>
        Рекомендуемые стельки
      </h3>
      ${insoleProducts.length > 0
        ? insoleProducts.map(productRow).join("")
        : `<div class="empty-note">Не указано</div>`}
    </div>

    <!-- Рекомендации -->
    <div class="bottom-card">
      <h3>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        Рекомендации
      </h3>
      ${noteRecommendations
        ? `<div class="conclusion-text">${escapeHtml(noteRecommendations).replace(/\n/g, "<br>")}</div>`
        : `<div class="empty-note">Не заполнено</div>`}
    </div>

    <!-- Список товаров -->
    <div class="bottom-card">
      <h3>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
        Список сопутствующих товаров
      </h3>
      ${accessoryProducts.length > 0
        ? accessoryProducts.map(productRow).join("")
        : `<div class="empty-note">Товары не выбраны</div>`}
    </div>

    <!-- Тип нагрузки -->
    <div class="bottom-card">
      <h3>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
        Тип нагрузки
      </h3>
      ${selectedLoadType
        ? `<div class="insole-name" style="font-size:13px">${selectedLoadType.name}</div>`
        : `<div class="empty-note">Не указано</div>`}
    </div>

    <!-- Комментарии -->
    <div class="bottom-card">
      <h3>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5c6bc0" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
        Комментарии
      </h3>
      ${noteComment
        ? `<div class="comments-text">${escapeHtml(noteComment).replace(/\n/g, "<br>")}</div>`
        : `<div class="empty-note">Не заполнено</div>`}
    </div>
  </div>

  ${allProducts.length ? `<div style="margin-top:12px;padding:12px 14px;background:#f5f5ff;border:1px solid #e0e0f5;border-radius:8px;text-align:right;font-weight:700">Общая сумма товаров: ${productsTotal === null ? "не все цены указаны" : `${productsTotal.toLocaleString("ru-RU")} ₽`}</div>` : ""}

  ${exercises.length > 0 ? `
  <!-- УПРАЖНЕНИЯ -->
  <div class="exercises-page">
    <div class="exercises-title">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a237e" stroke-width="2"><path d="M6.5 6.5 17.5 17.5"/><path d="m21 21-1-1"/><path d="m3 3 1 1"/><path d="m18 22 4-4"/><path d="m2 6 4-4"/><path d="m3 10 7-7"/><path d="m14 21 7-7"/></svg>
      Рекомендованные упражнения
    </div>
    <div class="exercises-grid">
      ${exercises.map(e => `
        <div class="exercise-card">
          ${e.photoDataUrl
            ? `<img src="${e.photoDataUrl}" class="exercise-photo" alt="${escapeHtml(e.name)}">`
            : `<div class="exercise-photo-empty">Нет фото</div>`}
          <div class="exercise-name">${escapeHtml(e.name)}</div>
          ${e.description ? `<div class="exercise-desc">${escapeHtml(e.description).replace(/\n/g, "<br>")}</div>` : ""}
          ${e.link ? `<a href="${escapeHtml(e.link)}" class="exercise-link" target="_blank" rel="noreferrer">Ссылка на инструкцию/видео</a>` : ""}
        </div>`).join("")}
    </div>
  </div>` : ""}

  <!-- ПОДВАЛ -->
  <div class="report-footer">
    <div class="footer-note">
      Отчет сформирован автоматически на основе данных сканирования и оценки специалиста.<br>
      Данный отчет не является диагнозом. Необходима консультация врача.
    </div>
    <div class="footer-id">ID отчёта: ${reportId}</div>
  </div>

</div>
<script>
  var imgs = document.querySelectorAll("img");
  var total = imgs.length;
  function doPrint() { setTimeout(function() { window.print(); }, 400); }
  if (total === 0) { doPrint(); }
  else {
    var done = 0;
    function tick() { done++; if (done >= total) doPrint(); }
    imgs.forEach(function(img) {
      if (img.complete) tick();
      else { img.onload = tick; img.onerror = tick; }
    });
  }
<\/script>
</body>
</html>`;
}

export function printReport(data) {
  const html = generateReportHtml(data);
  const blob = new Blob([html], { type: "text/html; charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "width=1020,height=800,scrollbars=yes");
  if (!win) {
    URL.revokeObjectURL(url);
    alert("Не удалось открыть окно. Разрешите всплывающие окна.");
    return;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function openSavedReport(reportHtml) {
  const blob = new Blob([reportHtml], { type: "text/html; charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "width=1020,height=800,scrollbars=yes");
  if (!win) {
    URL.revokeObjectURL(url);
    alert("Не удалось открыть окно. Разрешите всплывающие окна.");
    return;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
